/** Browser-independent controller sampling. Standard mapping only; raw input is
 * still exposed for diagnostics when a browser supplies a vendor mapping.
 */
export type PadLike={id:string;index:number;connected:boolean;mapping:string;
  axes:readonly number[];buttons:readonly {pressed:boolean;value:number}[]};
export type PadFrame={id:string;index:number;connected:boolean;standard:boolean;armed:boolean;
  axes:number[];buttons:number[];move:{x:number;z:number};pressed:number[];nav:number|null;activity:boolean};
export const EMPTY_PAD:PadFrame={id:"",index:-1,connected:false,standard:false,armed:false,
  axes:[],buttons:[],move:{x:0,z:0},pressed:[],nav:null,activity:false};
export const PAD_BUTTON={confirm:0,back:1,power:6,weapon:7,menu:9,up:12,down:13,left:14,right:15} as const;
const finite=(v:number,min:number,max:number)=>Number.isFinite(v)?Math.max(min,Math.min(max,v)):0;
export function padStick(x:number,z:number,deadzone=.15){
  x=finite(x,-1,1);z=finite(z,-1,1);
  const length=Math.hypot(x,z);
  if(length<=deadzone)return {x:0,z:0};
  const strength=Math.min(1,(length-deadzone)/(1-deadzone));
  return {x:x/length*strength,z:z/length*strength};
}
export class GamepadSampler{
  private identity="";
  private held:boolean[]=[];
  private previousAxes:number[]=[];
  private armed=false;
  private navigation:number|null=null;
  private repeatAt=0;
  reset(){this.identity="";this.held=[];this.previousAxes=[];this.armed=false;this.navigation=null;this.repeatAt=0;}
  sample(pads:readonly (PadLike|null)[],now:number,focused=true):PadFrame{
    const connected=pads.filter((p):p is PadLike=>!!p?.connected);
    const pad=connected.find(p=>`${p.index}:${p.id}`===this.identity) ??
      connected.find(p=>p.mapping==="standard")??connected[0];
    if(!pad){this.reset();return EMPTY_PAD;}
    const identity=`${pad.index}:${pad.id}`;
    if(identity!==this.identity){this.reset();this.identity=identity;}
    const axes=Array.from(pad.axes,v=>finite(v,-1,1));
    const buttons=Array.from(pad.buttons,b=>finite(Math.max(b.value,b.pressed?1:0),0,1));
    const down=buttons.map((v,i)=>v>(this.held[i] ? .4 : .55));
    const base={id:pad.id,index:pad.index,connected:true,standard:pad.mapping==="standard",axes,buttons};
    if(!focused){this.armed=false;this.held=down;this.navigation=null;return {...EMPTY_PAD,...base};}
    if(!this.armed){
      this.armed=axes.every(v=>Math.abs(v)<=.15)&&buttons.every(v=>v<=.25);
      this.held=down;this.previousAxes=axes;
      return {...EMPTY_PAD,...base,armed:this.armed};
    }
    const pressed=down.flatMap((v,i)=>v&&!this.held[i]?[i]:[]);
    const activity=pressed.length>0||axes.some((v,i)=>Math.abs(v-(this.previousAxes[i]??0))>.02);
    const direction=[12,13,14,15].find(i=>down[i])??null;
    let nav:number|null=null;
    if(direction!==this.navigation){this.navigation=direction;nav=direction;this.repeatAt=now+360;}
    else if(direction!==null&&now>=this.repeatAt){nav=direction;this.repeatAt=now+130;}
    this.held=down;if(activity)this.previousAxes=axes;
    return {...base,armed:true,move:base.standard?padStick(axes[0]??0,axes[1]??0):{x:0,z:0},
      pressed:base.standard?pressed:[],nav:base.standard?nav:null,activity:base.standard&&activity};
  }
}
